// Velkoobchodní spolupráce B2B (issue #38, screens/11-b2b.md + m11-b2b.md,
// mockupy design/„B2B.dc.html" a „B2B mobil.dc.html").
//
// Nativní stránka je `type-page`, jejíž obsah z administrace už NESE strukturu
// od předchozího správce (`.b2b-wrap`, `.b2b-hero`, `.b2b-card--standard|plus|
// premium`, `.b2b-off`, `.b2b-ribbon`, `.b2b-btm`, `hr.b2b-divider`). Procenta,
// popisy úrovní, e-mail i hero fotka jsou tedy REÁLNÁ data — čteme je odtud
// a jen je oblékáme do designu. Když blok v obsahu chybí, sekce se vypustí.
//
// ODCHYLKY OD SPECKY (ověřeno na dev instanci 2026-07-29):
//   • Tři „checky" v pricing kartách (Sleva 10 % z MOC / Přístup po registraci /
//     Bez minimálního odběru) jsou copy z návrhu bez opory v obsahu — vypuštěné.
//     Karta nese procento, popis z administrace a tagline.
//   • Obsah má v hero bloku DRUHÝ `h1` (kromě nativního nadpisu článku) — do
//     hera jde nativní hlavička, duplicitní nadpis mizí s ostatním nativním
//     textem pod `html.bs-info-ready`.
//   • „Vyžádat XML feed" je mailto na adresu z obsahu (žádný formulář neexistuje).

import { icon } from './icon.js';
import {
  el,
  link,
  textOf,
  fillRich,
  iconBox,
  pageHost,
  pageIs,
  markReady,
  buildHero,
  sectionHead,
  centerHead,
} from './infoPage.js';
import { page } from './pages.js';
import L from '../locale/cs/b2bPage.js';


// Texty → locale vrstva (S3 #399); dluh per soubor měří tools/lib/locale-debt.mjs
const LABEL_EYEBROW = L.eyebrow;
const LABEL_REGISTER = L.register;
const LABEL_TIERS_CTA = L.tiersCta;
const LABEL_NOTE = L.note;
const LABEL_HOW = L.how;
const LABEL_HOW_HINT = L.howHint;
const LABEL_TIERS = L.tiers;
const LABEL_TIERS_PEREX = L.tiersPerex;
const LABEL_BEST = L.best;
const LABEL_OFF = L.off;
const LABEL_FROM_MOC = L.fromMoc;
const LABEL_FEED_TAG = L.feedTag;
const LABEL_FEED_TAG_SUB = L.feedTagSub;
const LABEL_CONTACT_EYEBROW = L.contactEyebrow;
const LABEL_CONTACT_TITLE = L.contactTitle;
const LABEL_FEED_CTA = L.feedCta;
const LABEL_FEED_SUBJECT = L.feedSubject;

// Procesní kroky = copy z návrhu (v obsahu administrace nejsou)
const STEPS = [
  { icon: 'file', title: L.steps[0].title, desc: L.steps[0].desc },
  { icon: 'check', title: L.steps[1].title, desc: L.steps[1].desc },
  { icon: 'percent', title: L.steps[2].title, desc: L.steps[2].desc },
];

// Vzhled úrovní (bronz → stříbro → zlato) podle pořadí v obsahu
const TIER_SKINS = ['bronze', 'silver', 'gold'];
const TIER_ICONS = ['store', 'refresh', 'rosette'];

// ---------------------------------------------------------------------------
// Parsování obsahu z administrace
// ---------------------------------------------------------------------------
function parseTiers(host) {
  const cards = host.querySelectorAll('.b2b-card');
  const tiers = [];
  for (let i = 0; i < cards.length; i += 1) {
    const card = cards[i];
    const name = textOf(card.querySelector('.b2b-ribbon'));
    const offNode = card.querySelector('.b2b-off strong, .b2b-off b');
    const pct = offNode ? parseInt(textOf(offNode).replace(/\D/g, ''), 10) : NaN;
    if (!name || !isFinite(pct)) continue;
    // odstavce si vozíme jako UZLY, ne jako řetězce — inline `<strong>`
    // z administrace je součást sdělení (#20) a `textOf()` ho strhne
    const paras = [];
    card.querySelectorAll('p').forEach(function (p) {
      if (p.classList.contains('b2b-note')) return;
      if (textOf(p)) paras.push(p);
    });
    // seznam výhod úrovně (#19) — do parseru se dřív dostaly jen `p`,
    // takže i kdyby redaktor `ul/li` do administrace napsal, zmizely by
    const bullets = [];
    card.querySelectorAll('li').forEach(function (li) {
      if (textOf(li)) bullets.push(li);
    });
    tiers.push({
      name: name,
      pct: pct,
      eyebrow: textOf(card.querySelector('.b2b-card__top')),
      desc: paras,
      bullets: bullets,
      note: textOf(card.querySelector('.b2b-note')),
      tagline: textOf(card.querySelector('.b2b-btm span')),
      skin: TIER_SKINS[i] || TIER_SKINS[TIER_SKINS.length - 1],
      icon: TIER_ICONS[i] || TIER_ICONS[TIER_ICONS.length - 1],
    });
  }
  return tiers;
}

function parseFeed(host) {
  const heads = host.querySelectorAll('h2, h3');
  for (let i = 0; i < heads.length; i += 1) {
    if (!/feed/i.test(textOf(heads[i]))) continue;
    let node = heads[i].nextElementSibling;
    while (node && node.tagName !== 'P') node = node.nextElementSibling;
    const mail = node ? node.querySelector('a[href^="mailto:"]') : null;
    return {
      title: textOf(heads[i]),
      text: textOf(node),
      email: mail ? mail.getAttribute('href').replace(/^mailto:/, '') : null,
    };
  }
  return null;
}

// Kontaktní odstavec = ten za oddělovačem, který nese mailto a není o feedu
function parseContact(host) {
  const paras = host.querySelectorAll('p');
  for (let i = 0; i < paras.length; i += 1) {
    const mail = paras[i].querySelector('a[href^="mailto:"]');
    if (!mail) continue;
    const text = textOf(paras[i]);
    if (/feed|xml/i.test(text)) continue;
    return { text: text, email: mail.getAttribute('href').replace(/^mailto:/, '') };
  }
  return null;
}

function parseHero(host) {
  const hero = host.querySelector('.b2b-hero');
  if (!hero) return {};
  const cta = hero.querySelector('a[href]');
  const small = cta ? cta.querySelector('small') : null;
  const paras = [];
  hero.querySelectorAll('p').forEach(function (p) {
    const text = textOf(p);
    if (text) paras.push(text);
  });
  const img = hero.querySelector(':scope > img') || hero.querySelector('img:not(.b2b-cta__icon img)');
  return {
    perex: paras[0] || null,
    sub: paras[1] || null,
    ctaHref: cta ? cta.getAttribute('href') : null,
    ctaNote: small ? textOf(small) : null,
    image: img ? img.getAttribute('src') : null,
    imageAlt: img ? img.getAttribute('alt') || '' : '',
  };
}

// ---------------------------------------------------------------------------
// Stavba
// ---------------------------------------------------------------------------
function buildHeroSide(hero, maxPct, hasFeed) {
  if (!hero.image) return null;
  const side = el('div', 'bs-b2b__media');
  const frame = el('div', 'bs-b2b__frame');
  const img = el('img', 'bs-b2b__photo');
  img.src = hero.image;
  img.alt = hero.imageAlt;
  img.loading = 'lazy';
  frame.appendChild(img);
  side.appendChild(frame);

  if (isFinite(maxPct)) {
    const off = el('div', 'bs-b2b__float bs-b2b__float--off');
    off.appendChild(el('span', 'bs-b2b__float-num', L.maxOff(maxPct)));
    off.appendChild(el('span', 'bs-b2b__float-sub', LABEL_FROM_MOC));
    side.appendChild(off);
  }
  if (hasFeed) {
    const feed = el('div', 'bs-b2b__float bs-b2b__float--feed');
    feed.appendChild(iconBox('file', 'bs-b2b__float-icon'));
    const body = el('span', 'bs-b2b__float-body');
    body.appendChild(el('span', 'bs-b2b__float-title', LABEL_FEED_TAG));
    body.appendChild(el('span', 'bs-b2b__float-text', LABEL_FEED_TAG_SUB));
    feed.appendChild(body);
    side.appendChild(feed);
  }
  return side;
}

function buildSteps() {
  const block = el('section', 'bs-b2b__how');
  block.appendChild(sectionHead(LABEL_HOW, LABEL_HOW_HINT));
  const grid = el('div', 'bs-b2b__steps');
  STEPS.forEach(function (step, i) {
    const card = el('div', 'bs-b2b__step');
    card.appendChild(el('span', 'bs-b2b__step-num', String(i + 1)));
    card.appendChild(iconBox(step.icon, 'bs-b2b__step-icon'));
    card.appendChild(el('div', 'bs-b2b__step-name', step.title));
    card.appendChild(el('div', 'bs-b2b__step-desc', step.desc));
    grid.appendChild(card);
  });
  block.appendChild(grid);
  return block;
}

function buildTiers(tiers, note) {
  if (!tiers.length) return null;
  const section = el('section', 'bs-b2b__tiers');
  section.id = 'urovne';
  section.appendChild(centerHead(LABEL_TIERS, LABEL_TIERS_PEREX));

  let best = 0;
  tiers.forEach(function (t) {
    if (t.pct > best) best = t.pct;
  });

  const grid = el('div', 'bs-b2b__grid');
  tiers.forEach(function (tier) {
    const card = el('article', 'bs-b2b__card bs-b2b__card--' + tier.skin);
    const head = el('div', 'bs-b2b__card-head');
    if (tier.pct === best) head.appendChild(el('span', 'bs-b2b__badge', LABEL_BEST));
    head.appendChild(el('div', 'bs-b2b__card-eyebrow', tier.eyebrow || ''));
    head.appendChild(el('div', 'bs-b2b__card-name', tier.name));
    card.appendChild(head);

    const body = el('div', 'bs-b2b__card-body');
    const off = el('div', 'bs-b2b__off');
    off.appendChild(el('span', 'bs-b2b__off-label', LABEL_OFF));
    off.appendChild(el('span', 'bs-b2b__off-num', String(tier.pct)));
    off.appendChild(el('span', 'bs-b2b__off-pct', '%'));
    body.appendChild(off);

    tier.desc.forEach(function (node) {
      body.appendChild(fillRich(el('p', 'bs-b2b__card-text'), node));
    });

    if (tier.bullets && tier.bullets.length) {
      const list = el('ul', 'bs-b2b__card-list');
      tier.bullets.forEach(function (node) {
        const li = el('li', 'bs-b2b__card-li');
        li.appendChild(icon('check'));
        fillRich(li.appendChild(el('span')), node);
        list.appendChild(li);
      });
      body.appendChild(list);
    }

    if (tier.tagline) {
      const foot = el('div', 'bs-b2b__card-foot');
      foot.appendChild(iconBox(tier.icon, 'bs-b2b__card-tagicon'));
      foot.appendChild(el('span', 'bs-b2b__card-tagline', tier.tagline));
      body.appendChild(foot);
    }
    card.appendChild(body);
    grid.appendChild(card);
  });
  section.appendChild(grid);

  if (note) section.appendChild(el('div', 'bs-b2b__note', note));
  return section;
}

function buildBottom(contact, feed) {
  if (!contact && !feed) return null;
  const grid = el('section', 'bs-b2b__bottom');

  if (contact) {
    const card = el('div', 'bs-b2b__contact');
    card.appendChild(icon('headset', 'bs-b2b__contact-deco'));
    card.appendChild(el('span', 'bs-b2b__contact-eyebrow', LABEL_CONTACT_EYEBROW));
    card.appendChild(el('div', 'bs-b2b__contact-title', LABEL_CONTACT_TITLE));
    card.appendChild(el('p', 'bs-b2b__contact-text', contact.text));
    const cta = link('mailto:' + contact.email, 'bs-ip__cta bs-ip__cta--white bs-b2b__contact-cta');
    cta.appendChild(icon('mail'));
    cta.appendChild(el('span', null, contact.email));
    card.appendChild(cta);
    grid.appendChild(card);
  }

  if (feed) {
    const card = el('div', 'bs-b2b__feed');
    card.appendChild(iconBox('file', 'bs-b2b__feed-icon'));
    card.appendChild(el('div', 'bs-b2b__feed-title', feed.title));
    card.appendChild(el('p', 'bs-b2b__feed-text', feed.text));
    if (feed.email) {
      const cta = link(
        'mailto:' + feed.email + '?subject=' + encodeURIComponent(LABEL_FEED_SUBJECT),
        'bs-ip__cta bs-ip__cta--outline bs-b2b__feed-cta'
      );
      cta.appendChild(icon('mail'));
      cta.appendChild(el('span', null, LABEL_FEED_CTA));
      card.appendChild(cta);
    }
    grid.appendChild(card);
  }
  return grid;
}

// ---------------------------------------------------------------------------
export function initB2bPage() {
  if (!pageIs('b2b')) return;
  const host = pageHost();
  if (!host || document.querySelector('.bs-b2b')) return;

  const tiers = parseTiers(host.about);
  const hero = parseHero(host.about);
  const feed = parseFeed(host.about);
  const contact = parseContact(host.about);
  if (!tiers.length && !hero.perex) return; // obsah neodpovídá — necháme nativní text

  let best = 0;
  tiers.forEach(function (t) {
    if (t.pct > best) best = t.pct;
  });

  const root = el('div', 'bs-ip bs-b2b');
  root.appendChild(
    buildHero(
      {
        eyebrow: LABEL_EYEBROW,
        title: host.h1 || null,
        perex: hero.perex,
        sub: hero.sub,
        note: hero.ctaNote ? L.notePrefix + L.ctaNote(hero.ctaNote) + '.' : LABEL_NOTE,
        ctas: [
          {
            href: hero.ctaHref || page('register'),
            label: LABEL_REGISTER,
            icon: 'store',
            variant: 'primary',
          },
          { href: '#urovne', label: LABEL_TIERS_CTA, variant: 'outline' },
        ],
      },
      buildHeroSide(hero, best, !!feed)
    )
  );

  root.appendChild(buildSteps());

  const tierBlock = buildTiers(tiers, tiers.length ? tiers[0].note : null);
  if (tierBlock) root.appendChild(tierBlock);

  const bottom = buildBottom(contact, feed);
  if (bottom) root.appendChild(bottom);

  host.about.classList.add('bs-ip__native');
  host.about.insertAdjacentElement('beforebegin', root);
  markReady();
}
